import { Box, Card, CardContent, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import { useMemo } from 'react';

import { METRICS } from '../constants';
import { MetricKey, TimeSeriesPoint } from '../types';
import { filterByDateRange, formatMetricValue, getLatestValue, metricColor, toNumber } from '../utils';
import { TrendBadge } from './SonarMetricPrimitives';

const cardStyle = {
  borderRadius: 20,
  border: '1px solid #e2e8f0',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)',
  overflow: 'hidden' as const,
  maxWidth: '100%',
  boxSizing: 'border-box' as const,
};

export const SonarLatestRankingCard = ({
  data,
  metric,
}: {
  data: TimeSeriesPoint[];
  metric: MetricKey;
}) => {
  const filtered = useMemo(() => filterByDateRange(data, '', ''), [data]);
  const groupNames = [...new Set(filtered.map(point => point.metric))];
  const rows = groupNames
    .map(group => {
      const points = filtered
        .filter(point => point.metric === group)
        .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
      const rawCurrent = getLatestValue(points);
      const isRating = METRICS[metric].better === 'lower';
      const current = isRating && rawCurrent === 0 ? null : rawCurrent;
      const rawPrevious = points.length > 1 ? toNumber(points[points.length - 2].value) : null;
      const previous = isRating && rawPrevious === 0 ? null : rawPrevious;
      return { group, current, previous };
    })
    .filter(row => row.current !== null)
    .sort((a, b) =>
      METRICS[metric].better === 'higher'
        ? Number(b.current) - Number(a.current)
        : Number(a.current) - Number(b.current),
    )
    .slice(0, 8);

  if (!rows.length) return null;

  const max = METRICS[metric].max || Math.max(...rows.map(row => Math.abs(row.current || 0)), 1);

  return (
    <Card style={cardStyle}>
      <CardContent style={{ padding: 20 }}>
        <Typography style={{ color: '#0f172a', fontWeight: 900, fontSize: 18 }}>Latest ranking</Typography>
        <Typography style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Best groups for the selected metric.</Typography>
        <Box mt={2}>
          {rows.map(row => {
            const value = Math.max(0, Math.min(100, ((row.current || 0) / max) * 100));
            return (
              <Box key={row.group} mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                  <Tooltip title={row.group}>
                    <Typography style={{ color: '#1e293b', fontWeight: 850, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.group}
                    </Typography>
                  </Tooltip>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <TrendBadge current={row.current} previous={row.previous} metric={metric} />
                    <Typography style={{ color: metricColor(row.current, metric), fontWeight: 950, fontSize: 13, minWidth: 62, textAlign: 'right' }}>
                      {formatMetricValue(row.current, metric)}
                    </Typography>
                  </Box>
                </Box>
                <LinearProgress variant="determinate" value={value} style={{ height: 9, borderRadius: 99, backgroundColor: '#e2e8f0' }} />
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};